import React, { useCallback, useState, useMemo } from "react";
import {
  getFilteredSkills,
  mapRequirementToChatbotQuestion,
  requirementNeedsConfiguration
} from "../../Profile.utils";
import {
  RequirementHeader,
  RequirementInfo,
  Info,
  Dropdown,
  ToggleSwitch,
  customImportanceStyles,
  RadioButton,
  RobotIcon,
  MenuItem,
  MiniMenu,
  DotsBox,
  ReqCard,
  AdjustTag
} from "./Requirement.styled.js";
import {
  Collapse,
  SliderComponent,
  Modal,
  PosthogStore,
  useIsFeatureEnabled
} from "@talentbait/shared-components";
import { useTranslation } from "react-i18next";
import Actions from "../../../../Actions.js";
import { ChevronDown } from "@untitledui/icons/ChevronDown";
import { DotsVertical } from "@untitledui/icons/DotsVertical";
import { Group } from "../../../../styles/layout/index.js";
import Select from "react-select";
import logger from "../../../../utils/LoggerFrontend.js";
import { IMPORTANCE_OPTIONS } from "../../Profile.constants.js";
import { Popover, PopoverContent } from "../../../../Components/Overlays/Popover/Popover.jsx";
import { DeleteConfirm } from "../../modals/DeleteConfirm.jsx";
import { deleteModalStyles } from "../../modals/Modals.styled.js";
import withRefluxStore from "../../../../Stores/withRefluxStore.jsx";
import ProfilesStore from "../../Profile.store.js";
import ListOption from "./options-editor/ListOption.jsx";
import WeightedOptions from "./options-editor/WeightedOption.jsx";
import { BodyContainer, NoSkillsText } from "./options-editor/OptionsEditor.styled.js";
import { useEffect } from "react";

export const formatOptionLabel = (option, { selectValue, context }) => {
  const isSelected = selectValue.some(({ value }) => value === option.value);
  const isDropdown = context === "menu";

  if (!isDropdown) return option.label;

  return (
    <Group align="center" justify="space-between" width="100%" direction="row">
      <span>{option.label}</span>
      <RadioButton type="radio" checked={isSelected} readOnly />
    </Group>
  );
};

const Requirement = ({
  index,
  req,
  domainSkills,
  isOpen = false,
  onToggle = () => undefined,
  // store
  posthogInstance,
  profile
}) => {
  const { t } = useTranslation();
  const hasPermission = useIsFeatureEnabled("is-talentbait-team", posthogInstance);

  const [openCard, setOpenCard] = useState(index === 0);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const needsConfig = requirementNeedsConfiguration(req);

  const selectedImportance = useMemo(
    () => IMPORTANCE_OPTIONS(t).find((opt) => opt.value === req.importance),
    [req.importance, t]
  );

  const filteredSkills = useMemo(
    () => getFilteredSkills(req.category, domainSkills),
    [req.category, domainSkills]
  );

  const isCardOpen = needsConfig ? true : isOpen !== undefined ? isOpen : openCard;

  const toggleOpen = useCallback(
    (val) => {
      const newVal = typeof val === "boolean" ? val : !isCardOpen;

      if (typeof onToggle === "function") {
        onToggle(index, newVal);
      } else {
        setOpenCard(newVal);
      }
    },
    [onToggle, index, isCardOpen]
  );

  const stopPropagation = (e) => e.stopPropagation();

  const handleDeleteModal = useCallback((e) => {
    e.stopPropagation();
    setOpenDeleteModal((prev) => !prev);
  }, []);

  useEffect(() => {
    if (needsConfig && req.isAutomated) {
      const updatedRequirements = [...profile.requirements];
      updatedRequirements[index] = { ...req, isAutomated: false };

      logger.log("Requirement:useEffect", "update requirement isAutomated field", {
        updatedRequirements
      });

      const newUpdate = { requirements: updatedRequirements };

      Actions.patchProfile(profile.id, newUpdate);
    }
  }, [needsConfig, req, profile.id, profile.requirements, index]);

  const changeWeightImportance = useCallback(
    (selectedOption) => {
      if (req.importance === selectedOption.value) return;

      const updatedRequirements = [...profile.requirements];
      updatedRequirements[index] = { ...req, importance: selectedOption.value };

      logger.log("Requirement:handleImportance", "select requirement importance", {
        updatedRequirements
      });

      const update = { requirements: updatedRequirements };

      Actions.patchProfile(profile.id, update);
    },
    [req, profile.id, profile.requirements, index]
  );

  const deleteRequirement = useCallback(() => {
    const newUpdate = {
      requirements: profile.requirements.filter((_, idx) => idx !== index),
      chatbotQuestions: profile.chatbotQuestions || []
    };

    newUpdate.chatbotQuestions = newUpdate.chatbotQuestions.filter(
      (question) => question.name !== req.category
    );

    // Remove the corresponding chatbot question
    Actions.patchProfile(profile.id, newUpdate);
    setOpenDeleteModal(false);
  }, [profile.id, profile.requirements, profile.chatbotQuestions, index, req.category]);

  const addRequirementToChatbot = useCallback(
    (isChecked) => {
      const newUpdate = {
        requirements: [...profile.requirements],
        chatbotQuestions: [...profile.chatbotQuestions]
      };

      newUpdate.requirements[index] = { ...req, isAutomated: isChecked };

      if (!req.category && isChecked) {
        Actions.addNotification(t("The requirement is missing a category"), "error");
        return;
      }

      if (needsConfig && isChecked) {
        Actions.addNotification(t("The requirement still needs to be adjusted"), "error");
        return;
      }

      if (isChecked) {
        // Check if question already exists before adding
        const existingQuestionIndex = newUpdate.chatbotQuestions.findIndex(
          (q) => q.name === req.category
        );

        if (existingQuestionIndex === -1) {
          // Only add if it doesn't exist
          const newQuestion = mapRequirementToChatbotQuestion(req, filteredSkills);
          newUpdate.chatbotQuestions = [...newUpdate.chatbotQuestions, newQuestion];
        } else {
          // Update existing question
          newUpdate.chatbotQuestions[existingQuestionIndex] = mapRequirementToChatbotQuestion(
            req,
            filteredSkills
          );
        }
      } else {
        // Remove from chatbot questions
        newUpdate.chatbotQuestions = newUpdate.chatbotQuestions.filter(
          (q) => q.name !== req.category
        );
      }

      Actions.patchProfile(profile.id, newUpdate);
    },
    [
      t,
      profile.id,
      profile.requirements,
      profile.chatbotQuestions,
      req,
      index,
      filteredSkills,
      needsConfig
    ]
  );

  const displayOption = () => {
    if (req.type === "weightedOptions") {
      return <WeightedOptions index={index} req={req} filteredSkills={filteredSkills} />;
    }
    if (req.type === "list" || req.type === "text") {
      return <ListOption index={index} req={req} filteredSkills={filteredSkills} />;
    }
    return <NoSkillsText>{t("Unsupported type")}</NoSkillsText>;
  };

  return (
    <ReqCard tabIndex={0} $needsConfig={needsConfig}>
      {needsConfig && <AdjustTag>{t("Please adjust")}</AdjustTag>}
      <Collapse
        show={isCardOpen}
        parent={
          <RequirementHeader onClick={toggleOpen}>
            <RequirementInfo>
              <Info>
                {req.category} <span>{t("is")}</span>
              </Info>
              <div onClick={stopPropagation}>
                <Select
                  options={IMPORTANCE_OPTIONS(t)}
                  value={selectedImportance}
                  onChange={changeWeightImportance}
                  styles={customImportanceStyles}
                  formatOptionLabel={formatOptionLabel}
                  placeholder={t("Choose option")}
                />
              </div>
            </RequirementInfo>
            <Group direction="row" align="center" gap="5px">
              <ToggleSwitch onClick={stopPropagation}>
                <RobotIcon>🤖</RobotIcon>
                <label aria-label="automated-label" htmlFor={`automated-toggle-${index}`}>
                  {t("Automated")}
                </label>
                <SliderComponent
                  id={`automated-toggle-${index}`}
                  checked={req.isAutomated}
                  onChange={(e) => addRequirementToChatbot(e.target.checked)}
                  onColor="var(--primary-blue)"
                  offColor="var(--primary-grey-200)"
                />
              </ToggleSwitch>
              <Popover
                hoverOpen
                placement="bottom"
                trigger={
                  <DotsBox onClick={stopPropagation}>
                    <DotsVertical size={20} color="var(--primary-dark-300)" />
                  </DotsBox>
                }
              >
                <PopoverContent>
                  <MiniMenu>
                    {hasPermission && <MenuItem>{t("Edit")}</MenuItem>}
                    <MenuItem onClick={handleDeleteModal}>{t("Delete")}</MenuItem>
                  </MiniMenu>
                </PopoverContent>
              </Popover>
              <Dropdown
                $isOpen={isCardOpen}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleOpen();
                }}
              >
                <ChevronDown size={20} color="var(--primary-dark-300)" />
              </Dropdown>
            </Group>
          </RequirementHeader>
        }
      >
        <BodyContainer>{displayOption()}</BodyContainer>
      </Collapse>

      <Modal isOpen={openDeleteModal} setIsOpen={setOpenDeleteModal} styles={deleteModalStyles}>
        <DeleteConfirm
          header={t("Are you sure you want to delete this requirement?")}
          confirm={deleteRequirement}
          cancel={handleDeleteModal}
        />
      </Modal>
    </ReqCard>
  );
};

export default withRefluxStore(
  Requirement,
  [PosthogStore, ProfilesStore],
  ["posthogInstance", "profile"]
);
